import { Button, Input, useToast } from '@/components';
import { toggleInSet } from '@/lib/set';
import { ApiClientError } from '@uchat-team-management/shared';
import { Show, createSignal } from 'solid-js';
import { RoleMultiSelect } from './RoleMultiSelect';
import { useInviteUser, useRoles } from './hooks';

export const InviteUserForm = (props: { onDone: () => void; onCancel: () => void }) => {
  const roles = useRoles();
  const invite = useInviteUser();
  const toast = useToast();
  const [email, setEmail] = createSignal('');
  const [roleIds, setRoleIds] = createSignal<Set<string>>(new Set());
  const [error, setError] = createSignal<string | null>(null);

  const toggleRole = (roleId: string) => setRoleIds((cur) => toggleInSet(cur, roleId));

  const onSubmit = async (e: Event) => {
    e.preventDefault();
    setError(null);
    if (!email().trim() || roleIds().size === 0) {
      setError('Email and at least one role are required.');
      return;
    }
    try {
      await invite.mutateAsync({ email: email().trim(), roleIds: [...roleIds()] });
      toast.success('User added to allowlist');
      props.onDone();
    } catch (e) {
      if (e instanceof ApiClientError) {
        setError(e.error.message);
      } else {
        setError((e as Error).message ?? 'Invite failed');
      }
    }
  };

  return (
    <form onSubmit={onSubmit} class="flex flex-col gap-4">
      <Input
        label="Email"
        type="email"
        value={email()}
        onInput={(e) => setEmail(e.currentTarget.value)}
        required
        class="max-w-sm"
      />
      <RoleMultiSelect roles={roles.data?.data ?? []} selected={roleIds()} onToggle={toggleRole} />
      <Show when={error()}>
        <p class="text-xs text-red-600">{error()}</p>
      </Show>
      <div class="flex justify-end gap-2">
        <Button variant="ghost" onClick={props.onCancel} type="button">
          Cancel
        </Button>
        <Button type="submit" loading={invite.isPending}>
          Invite
        </Button>
      </div>
    </form>
  );
};
